import { useState, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { Case, CaseReportSignature, User } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import {
  cancelEditScreenDiagnoses,
  cancelModifyReportProcedure,
} from "shared/actions/caseDetailActions";
import { fetchCaseDisclaimers } from "shared/actions/caseReportActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { FeatureFlags, selectFeatureFlag } from "shared/reducers/sysReducer";
import { isCaseTCOnly } from "shared/utils/caseUtils";

import CaseReport, {
  DisclaimersObject,
} from "components/caseReport/CaseReport";
import { getDiagnosesObject } from "components/caseReport/utils";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import RecallReportButton from "components/RecallReportButton";
import Spinner from "components/Spinner";
import SignOutButton from "components/viewer/dialogs/SignOutButton";
import {
  canPublishTechnicalReport,
  canSignoutCase,
  isModifyingReport,
  isScreened,
} from "components/viewer/dialogs/utils";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

const CaseReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const caseDetailObject = useSelector(
    (state: ApplicationState) => state.caseDetail
  );
  const slideDetailObject = useSelector(
    (state: ApplicationState) => state.slideDetail
  );
  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const currentUser: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const { userSelectedLocationCode } = useSelector(
    ({ locationCodes }: ApplicationState) => locationCodes
  );
  const kwonKeys = useAppSelector((state) =>
    selectFeatureFlag(state, "kwonKeys")
  );

  const [finalSignature, setFinalSignature] = useState<
    CaseReportSignature | undefined
  >();

  const [isPrelimReport, setIsPrelimReport] = useState<boolean>(false);
  const [isTechnicalReport, setIsTechnicalReport] = useState<boolean>(
    (features?.technicalReports && isCaseTCOnly(caseDetailObject)) || false
  );
  const [disclaimers, setDisclaimers] = useState<DisclaimersObject[]>([]);

  const isModifying = isModifyingReport(
    caseDetailObject,
    features,
    currentUser
  );

  // TODO: This previewState and the ternary below are messy and duplicative
  // This component should render a single branch of `<CaseReport` which should
  // ... take different props depending on the situation. Note that the render
  // ... below doesn't even use the other previewStates other than "signout"
  // A problem with this is that the preview will render even if you "can't"
  // ... sign out the case (i.e. `canSignoutCase` is false), but it could be the
  // ... wrong preview.
  const previewState =
    canSignoutCase(caseDetailObject, currentUser, features?.signOutInViewer) ||
    canPublishTechnicalReport(caseDetailObject, currentUser, features)
      ? "signout"
      : isModifying?.source
      ? "addon"
      : "preview";

  const diagnoses = getDiagnosesObject(
    caseDetailObject.specimenDetail,
    previewState,
    currentUser
  );

  useEffect(() => {
    if (caseDetailObject?.id) {
      dispatch(fetchCaseDisclaimers({ case_id: caseDetailObject.id })).then(
        (fetchedDisclaimers) => {
          setDisclaimers(
            (fetchedDisclaimers[0].disclaimers as DisclaimersObject[]) || []
          );
        }
      );
    }
  }, [caseDetailObject.id]);

  const cancelModifyReportLoading = useIsLoading(
    `/cases/${caseDetailObject?.id}/modify_report/cancel`
  );

  const onHandleCancelReportModification = () => {
    caseDetailObject?.id &&
      dispatch(cancelModifyReportProcedure({ id: caseDetailObject.id }));
  };

  const onHandleCancelScreenEdit = () => {
    caseDetailObject.id &&
      dispatch(cancelEditScreenDiagnoses(caseDetailObject.id));
  };

  // NOTE: The <LogoLink> inside <CaseReport> "goes back",
  // ... so I did the same thing here. It might be better
  // ... to have logic around navigating to the case URL
  useHotkeys(
    "\\",
    () => navigate(-1),
    { enabled: kwonKeys, scopes: ["viewerHotkeys"] },
    []
  );

  return !caseDetailObject ? (
    <Spinner />
  ) : previewState === "signout" ? (
    <CaseReport
      disclaimersDryRun={disclaimers}
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      locationCode={userSelectedLocationCode}
      isPrelimReport={isPrelimReport}
      isTechnicalReport={
        (features?.technicalReportsOnly || isTechnicalReport) &&
        isCaseTCOnly(caseDetailObject)
      }
      actions={
        <>
          {!features?.screenPerSpecimen &&
            isScreened(caseDetailObject.specimenDetail[0]?.diagnoses) &&
            caseDetailObject.specimenDetail[0]?.diagnoses.find(
              (diag) =>
                diag.user &&
                diag.user.id === currentUser?.id &&
                !diag.is_completed
            ) && (
              <PathologyWatchButton
                color="red"
                onClick={onHandleCancelScreenEdit}
              >
                Cancel Screen Edit
              </PathologyWatchButton>
            )}

          {caseDetailObject.source === Case.source.PW &&
            canPublishTechnicalReport(
              caseDetailObject,
              currentUser,
              features
            ) && (
              <label className="Input__checkbox">
                <input
                  type="checkbox"
                  onChange={() => {
                    setIsTechnicalReport((s) => !s);
                    setIsPrelimReport(false);
                  }}
                  checked={isTechnicalReport}
                />
                Preview Technical Report
              </label>
            )}
          {features?.issuePrelimReports &&
            !features?.technicalReportsOnly &&
            caseDetailObject.source === Case.source.PW && (
              <label className="Input__checkbox">
                <input
                  type="checkbox"
                  onChange={() => {
                    setIsPrelimReport((s) => !s);
                    setIsTechnicalReport(false);
                  }}
                  checked={isPrelimReport}
                />
                Preview Preliminary Report
              </label>
            )}
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            isPrelim={isPrelimReport}
            signOutTipPlace="left"
            isTechnical={
              (features?.technicalReportsOnly || isTechnicalReport) &&
              isCaseTCOnly(caseDetailObject)
            }
          />
        </>
      }
    />
  ) : isModifying?.source ? (
    <CaseReport
      disclaimersDryRun={disclaimers}
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      useChangeAddon={isModifying.source !== CaseDiagnosis.source.CORRECTION}
      actions={
        <>
          <PathologyWatchButton
            onClick={onHandleCancelReportModification}
            color="red"
            disabled={cancelModifyReportLoading}
          >
            {`Cancel ${isModifying.source}`}
          </PathologyWatchButton>
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            signOutTipPlace="left"
            label={`${
              isModifying.source === CaseDiagnosis.source.CORRECTION
                ? "Correct"
                : isModifying.source
            } Report`}
          />
        </>
      }
    />
  ) : (
    // TODO: Investigate what this does, exactly. LIS_PENDING preview state has been broken for a while,
    // ... as has the "diagnosis dialog" (I think it always renders like there is no diagnosis)
    // In an LIS_PENDING state, we should strive to provide a serialization from the pending report,
    // ... so it's less of a "preview" and more of a `<FinalReportPage` for the report
    <CaseReport
      disclaimersDryRun={disclaimers}
      diagnoses={diagnoses}
      currentUser={currentUser}
      isUnsigned
      features={features}
      caseDetailObject={caseDetailObject}
      actions={
        caseDetailObject.status === Case.status.LIS_PENDING ? (
          <RecallReportButton
            currentUser={currentUser}
            caseDetail={caseDetailObject}
          />
        ) : undefined
      }
    />
  );
};

export default CaseReportPage;
